import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Download, FileSpreadsheet, GraduationCap, Heart, Loader2, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DataExportPage() {
  const [anonymize, setAnonymize] = useState(true);
  const [loadingAcademy, setLoadingAcademy] = useState(false);
  const [loadingTreatment, setLoadingTreatment] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { endpoint, filename, setLoading }

  const downloadCSV = async (endpoint, filename, setLoading, skipConfirm = false) => {
    // If identified and not confirmed, show double-confirmation dialog
    if (!anonymize && !skipConfirm) {
      setConfirmDialog({ endpoint, filename, setLoading });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ anonymize: String(anonymize) });
      if (!anonymize) params.set('confirmed_identified', 'true');

      const res = await fetch(`${API}/api/admin/${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        const data = await res.json();
        toast.error(data.detail || 'Exportação identificada bloqueada');
        return;
      }
      if (!res.ok) throw new Error('Erro');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} exportado com sucesso! Auditoria registrada.`);
    } catch (err) {
      toast.error('Erro ao exportar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmIdentified = () => {
    if (!confirmDialog) return;
    setConfirmDialog(null);
    downloadCSV(confirmDialog.endpoint, confirmDialog.filename, confirmDialog.setLoading, true);
  };

  return (
    <div data-testid="admin-data-export-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-blue-500" />
          Exportação de Dados Científicos
        </h1>
        <p className="text-sm text-zinc-400 mt-1">Dados para suporte à tese sobre Enantiodromia</p>
      </div>

      {/* LGPD Controls */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-amber-400" />
              <div>
                <Label className="text-white font-medium">Anonimização (LGPD)</Label>
                <p className="text-xs text-zinc-400">Quando ativo, nomes e dados clínicos são removidos</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">{anonymize ? 'Anonimizado' : 'Identificado'}</span>
              <Switch checked={anonymize} onCheckedChange={setAnonymize} data-testid="anonymize-toggle" />
            </div>
          </div>
          {!anonymize && (
            <Badge className="mt-3 bg-red-500/20 text-red-400 text-[10px]">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Dados identificados — Requer confirmação dupla. Use apenas para fins autorizados conforme LGPD.
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Fields info */}
      <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
        <CardContent className="p-4">
          <p className="text-xs text-zinc-400 font-semibold mb-2">Campos exportados ({anonymize ? 'modo anonimizado' : 'modo identificado'}):</p>
          <div className="grid md:grid-cols-2 gap-4 text-xs text-zinc-500">
            <div>
              <p className="text-zinc-300 font-medium mb-1">Academia PAREI-AD®:</p>
              <p>user_id{anonymize ? ' (parcial)' : ''}, {!anonymize ? 'user_name, ' : ''}module_num, score_pct, passed, attempt_num, date</p>
            </div>
            <div>
              <p className="text-zinc-300 font-medium mb-1">Evolução Tratamentos:</p>
              {anonymize
                ? <p>user_id (parcial), date</p>
                : <p>user_id, user_name, date, mood, craving_level, triggers</p>
              }
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <GraduationCap className="h-5 w-5 text-violet-400" />
              Academia PAREI-AD®
            </CardTitle>
            <CardDescription className="text-zinc-400">Respostas e progresso dos módulos do curso profissional</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => downloadCSV('export/academy-progress', 'academia_parei_ad.csv', setLoadingAcademy)}
              disabled={loadingAcademy}
              className="w-full bg-violet-600 hover:bg-violet-700"
              data-testid="export-academy-btn"
            >
              {loadingAcademy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Exportar CSV {!anonymize && '(Identificado)'}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <Heart className="h-5 w-5 text-rose-400" />
              Evolução dos Tratamentos
            </CardTitle>
            <CardDescription className="text-zinc-400">Check-ins e dados estatísticos de evolução</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => downloadCSV('export/treatment-evolution', 'evolucao_tratamento.csv', setLoadingTreatment)}
              disabled={loadingTreatment}
              className="w-full bg-rose-600 hover:bg-rose-700"
              data-testid="export-treatment-btn"
            >
              {loadingTreatment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Exportar CSV {!anonymize && '(Identificado)'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Double Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(v) => { if (!v) setConfirmDialog(null); }}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Confirmação de Exportação Identificada
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Você está prestes a exportar dados <strong className="text-white">com identificação pessoal</strong>. 
              Esta ação será registrada na auditoria com seu email, data/hora e tipo de exportação.
              Use apenas para fins autorizados conforme a LGPD.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-zinc-600 text-zinc-300" onClick={() => setConfirmDialog(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleConfirmIdentified} data-testid="confirm-identified-export">
              Confirmo — Exportar Identificado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
